import React, { useState, useEffect, useRef } from 'react';
import { ArrowUpDown, Building2, ChevronDown, Grid2X2, House, List, MapPin, Phone, Share2, Sprout, Star, UserX } from 'lucide-react';
import { FaFacebookF, FaInstagram, FaTiktok } from 'react-icons/fa6';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import ReportUserModal from '../components/ReportUserModal';
import ImageViewer from '../components/ImageViewer';
import LoadingIndicator from '../components/LoadingIndicator';
import EmptyState from '../components/EmptyState';
import AccountTypeInfo from '../components/AccountTypeInfo';
import { fetchProducts } from '../services/product.api';
import { fetchUserById, reportUser } from '../services/user.api';
import { Product, User } from '../services/api.types';
import { getAccessToken, getUserId } from '../services/auth';
import { trackEvent } from '../services/analytics';
import { useTranslations } from '../translations';
import { useNotification } from '../components/NotificationProvider';
import BackButton from '../components/BackButton';
import { createPageUrl } from '../utils/appRoutes';
import { shareUrl } from '../utils/share';
import { createUserShareUrl } from '../utils/shareUrls';
import { getUserPublicName } from '../utils/userSeo';
import { getPaginationItems } from '../utils/pagination';
import './UserDetails.css';
import '../components/ThemedDropdown.css';

interface UserDetailsProps {
  isLoggedIn: boolean;
  isAdmin?: boolean;
  userId: string;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToProductDetail: (productId: string) => void;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
  onNavigateToAdministration?: () => void;
}

const USER_PRODUCTS_PAGE_SIZE = 18;

const UserDetails: React.FC<UserDetailsProps> = ({
  isLoggedIn,
  isAdmin = false,
  userId,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProductDetail,
  onLogout,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
  onNavigateToAdministration
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [user, setUser] = useState<User | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'BUY' | 'SELL'>('SELL');
  const [viewType, setViewType] = useState<'list' | 'grid'>('list');
  const [sortBy, setSortBy] = useState('Cele mai noi');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [showReportUserModal, setShowReportUserModal] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const phoneRevealTrackedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!showSortDropdown) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!sortDropdownRef.current?.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSortDropdown]);

  useEffect(() => {
    let active = true;
    const metadataCleanups: Array<() => void> = [];
    const originalTitle = document.title;

    const updateMetaTag = (attribute: 'name' | 'property', key: string, content: string) => {
      let meta = document.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;
      if (meta) {
        const previousContent = meta.content;
        metadataCleanups.push(() => {
          meta!.content = previousContent;
        });
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, key);
        document.head.appendChild(meta);
        const createdMeta = meta;
        metadataCleanups.push(() => createdMeta.remove());
      }
      meta.content = content;
    };

    const updateCanonicalUrl = (url: string) => {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (canonical) {
        const previousHref = canonical.href;
        metadataCleanups.push(() => {
          canonical!.href = previousHref;
        });
      } else {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
        const createdCanonical = canonical;
        metadataCleanups.push(() => createdCanonical.remove());
      }
      canonical.href = url;
    };

    const loadUserData = async () => {
      setLoading(true);
      setShowPhoneNumber(false);
      try {
        const token = isLoggedIn ? getAccessToken() : null;
        const userData = await fetchUserById(userId, token);
        if (!active) return;

        setUser(userData);

        const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
          || 'Utilizator roade.ro';
        const fallbackDescription = `Vezi profilul si anunturile utilizatorului ${fullName} pe roade.ro.`;
        const rawDescription = userData.description?.trim() || fallbackDescription;
        const description = rawDescription.length > 160
          ? `${rawDescription.substring(0, 157)}...`
          : rawDescription;
        const publicName = getUserPublicName(userData);
        const userPath = createPageUrl('userDetails', { id: userId, name: publicName });
        const userUrl = `${window.location.origin}${userPath}`;

        if (window.location.pathname !== userPath || window.location.search) {
          window.history.replaceState({}, '', userPath);
        }

        document.title = `${publicName} | roade.ro`;
        updateCanonicalUrl(userUrl);
        updateMetaTag('name', 'description', description);
        updateMetaTag(
          'name',
          'robots',
          !userData.status || userData.status === 'ACTIVE' ? 'index, follow' : 'noindex, nofollow',
        );
        updateMetaTag('property', 'og:type', 'profile');
        updateMetaTag('property', 'og:url', userUrl);
        updateMetaTag('property', 'og:site_name', 'roade.ro');
        updateMetaTag('property', 'og:title', publicName);
        updateMetaTag('property', 'og:description', description);
        updateMetaTag('property', 'og:locale', 'ro_RO');
        updateMetaTag('property', 'profile:first_name', userData.firstName || '');
        updateMetaTag('property', 'profile:last_name', userData.lastName || '');
        updateMetaTag('name', 'twitter:card', 'summary');
        updateMetaTag('name', 'twitter:title', publicName);
        updateMetaTag('name', 'twitter:description', description);

        if (userData.imageUrl) {
          updateMetaTag('property', 'og:image', userData.imageUrl);
          updateMetaTag('property', 'og:image:secure_url', userData.imageUrl);
          updateMetaTag('property', 'og:image:alt', publicName);
          updateMetaTag('name', 'twitter:image', userData.imageUrl);
          updateMetaTag('name', 'twitter:image:alt', publicName);
        }

        document.getElementById('profile-structured-data')?.remove();
        if (!userData.status || userData.status === 'ACTIVE') {
          const sameAs = [userData.facebook, userData.instagram, userData.tiktok]
            .filter((value): value is string => Boolean(value));
          const structuredData = {
            '@context': 'https://schema.org',
            '@type': 'ProfilePage',
            url: userUrl,
            mainEntity: {
              '@type': userData.companyName ? 'Organization' : 'Person',
              name: publicName,
              identifier: userData.id,
              description,
              ...(userData.imageUrl ? { image: userData.imageUrl } : {}),
              ...(sameAs.length > 0 ? { sameAs } : {}),
            },
          };
          const jsonLd = document.createElement('script');
          jsonLd.id = 'profile-structured-data';
          jsonLd.type = 'application/ld+json';
          jsonLd.text = JSON.stringify(structuredData);
          document.head.appendChild(jsonLd);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    loadUserData();

    return () => {
      active = false;
      document.title = originalTitle;
      document.getElementById('profile-structured-data')?.remove();
      metadataCleanups.reverse().forEach((cleanup) => cleanup());
    };
  }, [userId, isLoggedIn]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, sortBy]);

  useEffect(() => {
    const loadProducts = async () => {
      if (!userId) return;
      setProductsLoading(true);
      try {
        const productType = activeTab === 'BUY' ? 'DEMAND' : 'PRODUCT';
        const { sortField, order } = getSortParams();
        const data = await fetchProducts(
          currentPage,
          USER_PRODUCTS_PAGE_SIZE,
          productType,
          null,
          [],
          userId,
          sortField,
          order,
        );
        setProducts(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setProductsLoading(false);
      }
    };
    loadProducts();
  }, [userId, activeTab, currentPage, sortBy]);

  const handleBack = () => {
    window.history.back();
  };

  const handleShareUser = async () => {
    if (!user) return;

    const userUrl = createUserShareUrl(
      window.location.origin,
      user.id,
      getUserPublicName(user)
    );

    try {
      const result = await shareUrl({
        url: userUrl
      });
      if (result === 'copied') {
        showNotification(t.userDetails.shareLinkCopied, 'success');
      }
    } catch (error) {
      console.error('Error sharing user:', error);
      showNotification(t.userDetails.shareError, 'error');
    }
  };

  const sortOptions = [
    { value: 'Cele mai noi', label: t.searchResults.newest },
    { value: 'Cele mai vechi', label: t.searchResults.oldest },
    { value: 'Pret crescator', label: t.searchResults.priceAscending },
    { value: 'Pret descrescator', label: t.searchResults.priceDescending },
  ];

  const selectedSortLabel = sortOptions.find((option) => option.value === sortBy)?.label ?? t.searchResults.newest;

  const getSortParams = () => {
    switch (sortBy) {
      case 'Cele mai vechi':
        return { sortField: 'PUBLISHED_AT', order: 'ASC' as const };
      case 'Pret crescator':
        return { sortField: 'PRICE', order: 'ASC' as const };
      case 'Pret descrescator':
        return { sortField: 'PRICE', order: 'DESC' as const };
      case 'Cele mai noi':
      default:
        return { sortField: 'PUBLISHED_AT', order: 'DESC' as const };
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReportUserClick = () => {
    if (!isLoggedIn) {
      onNavigateToLogin();
      return;
    }
    setShowReportUserModal(true);
  };

  const handleReportUserSubmit = async (reason: string, otherText?: string) => {
    try {
      const reasonMap: Record<string, string> = {
        spam: t.reportUserModal.spam,
        inappropriateContent: t.reportUserModal.inappropriateContent,
        harassment: t.reportUserModal.harassment,
        scam: t.reportUserModal.scam,
        fakeProfile: t.reportUserModal.fakeProfile,
        sellerStatus: t.reportUserModal.sellerStatus,
      };
      const reasonText = reason === 'other' && otherText ? otherText : (reasonMap[reason] || reason);

      await reportUser(userId, [reasonText]);
      setShowReportUserModal(false);
      showNotification(t.reportUserModal.successMessage, 'success');
    } catch (error) {
      console.error('Error reporting user:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      setShowReportUserModal(false);
      showNotification(t.reportUserModal.errorMessage, 'error');
    }
  };

  const handleReportUserClose = () => {
    setShowReportUserModal(false);
  };

  const renderPagination = () => {
    const pages = getPaginationItems(currentPage, totalPages);
    const compactPages = getPaginationItems(currentPage, totalPages, true);

    const renderPages = (items: ReturnType<typeof getPaginationItems>) => items.map((page) => (
      typeof page !== 'number' ? (
        <span key={page} className="user-details-pagination-ellipsis">...</span>
      ) : (
        <button
          type="button"
          key={page}
          className={`user-details-pagination-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageChange(page)}
          aria-current={currentPage === page ? 'page' : undefined}
          aria-label={`Pagina ${page}`}
        >
          {page}
        </button>
      )
    ));

    return (
      <div className="user-details-pagination">
        <button
          type="button"
          className="user-details-pagination-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Pagina anterioară"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.0005 4.99993C14.2265 4.99993 14.4535 5.07593 14.6405 5.23193C15.0645 5.58493 15.1215 6.21593 14.7685 6.63993L10.2925 12.0109L14.6075 17.3729C14.9535 17.8029 14.8855 18.4329 14.4555 18.7789C14.0245 19.1249 13.3955 19.0569 13.0485 18.6269L8.22051 12.6269C7.92251 12.2559 7.92651 11.7259 8.23151 11.3599L13.2315 5.35993C13.4295 5.12293 13.7135 4.99993 14.0005 4.99993Z" fill="currentColor"/>
          </svg>
        </button>

        <div className="user-details-pagination-pages user-details-pagination-pages-desktop">
          {renderPages(pages)}
        </div>
        <div className="user-details-pagination-pages user-details-pagination-pages-mobile">
          {renderPages(compactPages)}
        </div>

        <button
          type="button"
          className="user-details-pagination-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Pagina următoare"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.99951 19.0001C9.77351 19.0001 9.54651 18.9241 9.35951 18.7681C8.93551 18.4151 8.87851 17.7841 9.23151 17.3601L13.7075 11.9891L9.39251 6.62708C9.04651 6.19708 9.11451 5.56708 9.54451 5.22108C9.97551 4.87508 10.6045 4.94308 10.9515 5.37308L15.7795 11.3731C16.0775 11.7441 16.0735 12.2741 15.7685 12.6401L10.7685 18.6401C10.5705 18.8771 10.2865 19.0001 9.99951 19.0001Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="user-details-page">
        <Header
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onMessagesClick={onNavigateToMessages}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
          onAdministrationClick={onNavigateToAdministration}
        />
        <main className="user-details-content">
          <div className="user-details-container user-details-loading-layout">
            <div className="user-details-sidebar-wrapper">
              <BackButton className="user-details-back-button" onClick={handleBack} />
              <section className="user-details-profile-card user-details-loading-profile-card">
                <LoadingIndicator message={t.userDetails.loading} />
              </section>
            </div>
            <section className="user-details-products-section user-details-loading-products-section">
              <LoadingIndicator message={t.userDetails.loadingAnnouncements} />
            </section>
          </div>
        </main>
        <Footer
          onNavigateToTerms={onNavigateToTerms}
          onNavigateToPrivacy={onNavigateToPrivacy}
          onNavigateToCookies={onNavigateToCookies}
          onNavigateToAbout={onNavigateToAbout}
          onNavigateToContact={onNavigateToContact}
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-details-page">
        <Header
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onMessagesClick={onNavigateToMessages}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
          onAdministrationClick={onNavigateToAdministration}
        />
        <main className="user-details-error-container">
          <EmptyState
            icon={<UserX />}
            title={t.userDetails.notFound}
            description={t.userDetails.notFoundDescription}
          >
            <button className="app-empty-state-primary" type="button" onClick={onNavigateToHome}>
              <House size={17} aria-hidden="true" />
              {t.userDetails.notFoundAction}
            </button>
          </EmptyState>
        </main>
        <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
      />
      </div>
    );
  }

  const phoneNumber = user.phoneNumber?.trim() || '';
  const phoneDisplay = showPhoneNumber && isLoggedIn
    ? phoneNumber || t.userDetails.phoneUnavailable
    : '****';

  return (
    <div className="user-details-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
        onAdministrationClick={onNavigateToAdministration}
      />

      <main className="user-details-content">
        <div className="user-details-container">
          <div className="user-details-sidebar-wrapper">
            <BackButton className="user-details-back-button" onClick={handleBack} />

            <div className="user-details-profile-card">
              <div className="user-details-profile-identity">
                <span className="user-details-profile-eyebrow">
                  <Sprout size={15} aria-hidden="true" />
                  {t.userDetails.localProducer}
                </span>
                <div className="user-details-profile-avatar">
                  {user.imageUrl ? (
                    <img
                      src={user.imageUrl}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="user-details-avatar-img"
                      onClick={() => setShowImageViewer(true)}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <div className="user-details-avatar-placeholder-large">
                      {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                    </div>
                  )}
                </div>

                <div className="user-details-identity-text">
                  <h2 className="user-details-profile-greeting">{t.userDetails.greeting.replace('{name}', user.firstName)}</h2>
                  {user.companyName && (
                    <div
                      className="user-details-company-name"
                      aria-label={`${t.userDetails.companyName}: ${user.companyName}`}
                    >
                      <Building2 size={17} aria-hidden="true" />
                      <span>{user.companyName}</span>
                    </div>
                  )}
                  <div className="user-details-profile-highlights">
                    <span className="user-details-rating-pill">
                      <Star size={14} fill="currentColor" aria-hidden="true" />
                      {user.rate.toLocaleString('ro-RO', { maximumFractionDigits: 1 })}
                    </span>
                    <span className="user-details-announcement-pill">
                      {t.userDetails.activeAnnouncements.replace('{count}', totalElements.toString())}
                    </span>
                  </div>
                  <p className="user-details-rating-disclosure">{t.productDetail.ratingNotVerified}</p>
                </div>
              </div>

              <AccountTypeInfo accountType={user.accountType} companyDetails={user} />

              {user.description && (
                <div className="user-details-profile-section">
                  <h3 className="user-details-section-heading">{t.userDetails.about}</h3>
                  <p className="user-details-profile-bio">
                    {user.description}
                  </p>
                </div>
              )}

              <div className="user-details-profile-section">
                <h3 className="user-details-section-heading">{t.userDetails.contact}</h3>
                <div className="user-details-contact-info">
                  <div className="user-details-contact-item user-details-phone-item">
                    <Phone aria-hidden="true" />
                    <span className="user-details-phone-value">{phoneDisplay}</span>
                    <button
                      type="button"
                      className="user-details-show-phone-button"
                      onClick={() => {
                        if (!isLoggedIn) {
                          onNavigateToLogin();
                        } else {
                          if (!showPhoneNumber && phoneNumber && phoneRevealTrackedForRef.current !== userId
                            && userId !== getUserId()) {
                            phoneRevealTrackedForRef.current = userId;
                            trackEvent('contact_seller', { contact_method: 'phone_reveal', source_surface: 'seller_profile' });
                          }
                          setShowPhoneNumber(current => !current);
                        }
                      }}
                    >
                      {showPhoneNumber && isLoggedIn ? t.userDetails.hidePhone : t.userDetails.showPhone}
                    </button>
                  </div>

                  {user.location && (
                    <div className="user-details-contact-item">
                      <MapPin aria-hidden="true" />
                      <span>{user.location.name}, {user.location.county.name}</span>
                    </div>
                  )}

                  {(user.facebook || user.instagram || user.tiktok) && (
                    <div className="user-details-social-links">
                      {user.facebook && (
                        <a
                          href={user.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="user-details-social-link"
                          aria-label="Facebook"
                        >
                          <FaFacebookF className="user-details-social-icon" aria-hidden="true" />
                        </a>
                      )}

                      {user.instagram && (
                        <a
                          href={user.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="user-details-social-link"
                          aria-label="Instagram"
                        >
                          <FaInstagram className="user-details-social-icon" aria-hidden="true" />
                        </a>
                      )}

                      {user.tiktok && (
                        <a
                          href={user.tiktok}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="user-details-social-link"
                          aria-label="TikTok"
                        >
                          <FaTiktok className="user-details-social-icon" aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="user-details-action-divider"></div>

              <button
                type="button"
                className="user-details-share-button user-details-share-button-bottom"
                onClick={handleShareUser}
                aria-label={t.userDetails.share}
                title={t.userDetails.share}
              >
                <Share2 size={16} strokeWidth={2} aria-hidden="true" />
                <span>{t.userDetails.share}</span>
              </button>

              <div className="user-details-action-divider"></div>

              <div className="product-detail-report-button" onClick={handleReportUserClick}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M9.11267 0.951333C9.472 0.834 9.87067 0.897333 10.1787 1.12C10.4847 1.342 10.6667 1.69733 10.6667 2.07067V7.83267C10.6667 8.268 10.422 8.66533 10.0273 8.87C9.61533 9.08133 8.87267 9.33333 7.66667 9.33333C6.66657 9.33333 5.86559 9.01969 5.09133 8.71652L5.09 8.716L5.08848 8.71541C4.39971 8.44562 3.74878 8.19067 3 8.19067C2.11533 8.19067 1.59667 8.35067 1.33333 8.468V11.3333C1.33333 11.702 1.03533 12 0.666667 12C0.298 12 0 11.702 0 11.3333V8.09533V6V1.238C0 1.05933 0.072 0.887333 0.2 0.762667C0.33 0.634667 1.07533 0 3 0C4.00009 0 4.80108 0.31364 5.57533 0.616811L5.57667 0.617333L5.57819 0.617931C6.26696 0.88771 6.91789 1.14267 7.66667 1.14267C8.22867 1.14267 8.72867 1.076 9.11267 0.951333ZM7.66667 8C8.55 8 9.06933 7.842 9.33333 7.724V2.276C8.85067 2.40867 8.292 2.476 7.66667 2.476C6.66657 2.476 5.86559 2.16236 5.09133 1.85919L5.09 1.85867L5.08847 1.85807C4.3997 1.58829 3.74878 1.33333 3 1.33333C2.118 1.33333 1.59733 1.49267 1.33333 1.61067V6V7.056C1.74933 6.942 2.29533 6.85733 3 6.85733C4.00009 6.85733 4.80108 7.17097 5.57534 7.47415L5.57667 7.47467L5.57819 7.47526C6.26696 7.74504 6.91789 8 7.66667 8Z" fill="#B12A00" transform="translate(3, 2)"/>
                </svg>
                <span className="product-detail-report-label">Raportează</span>
              </div>
            </div>
          </div>

          <div className="user-details-products-section">
            <div className="user-details-products-heading">
              <span className="user-details-products-eyebrow">{t.userDetails.freshFromProducer}</span>
              <h1 className="user-details-page-title">{t.userDetails.announcements.replace('{name}', user.firstName)}</h1>
              <p className="user-details-products-subtitle">{t.userDetails.announcementsSubtitle}</p>
            </div>

            <div className="user-details-controls-row">
              <div className="user-details-tabs">
                <button
                  className={`user-details-tab ${activeTab === 'SELL' ? 'user-details-tab-active' : ''}`}
                  onClick={() => setActiveTab('SELL')}
                >
                  {t.searchResults.tabSell}
                </button>
                <button
                  className={`user-details-tab ${activeTab === 'BUY' ? 'user-details-tab-active' : ''}`}
                  onClick={() => setActiveTab('BUY')}
                >
                  {t.searchResults.tabBuy}
                </button>
              </div>

              <div className="user-details-products-controls">
                <div className="user-details-sort-control">
                  <span className="user-details-control-label">{t.searchResults.orderBy}</span>
                  <div className="user-details-sort-dropdown-wrapper" ref={sortDropdownRef}>
                    <button
                      type="button"
                      className="user-details-sort-dropdown-button"
                      onClick={() => setShowSortDropdown((isOpen) => !isOpen)}
                      aria-haspopup="listbox"
                      aria-expanded={showSortDropdown}
                    >
                      <ArrowUpDown className="user-details-sort-icon" size={16} aria-hidden="true" />
                      <span className="user-details-sort-value">{selectedSortLabel}</span>
                      <ChevronDown
                        className={`user-details-sort-chevron themed-dropdown-chevron ${showSortDropdown ? 'themed-dropdown-chevron-open' : ''}`}
                        size={18}
                        aria-hidden="true"
                      />
                    </button>
                    {showSortDropdown && (
                      <div className="user-details-sort-dropdown-menu themed-dropdown-menu" role="listbox">
                        {sortOptions.map((option) => (
                          <button
                            type="button"
                            key={option.value}
                            className={`user-details-sort-option themed-dropdown-option ${sortBy === option.value ? 'themed-dropdown-option-active' : ''}`}
                            role="option"
                            aria-selected={sortBy === option.value}
                            onClick={() => {
                              setSortBy(option.value);
                              setShowSortDropdown(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="user-details-view-toggle">
                  <span className="user-details-control-label">{t.searchResults.viewType}</span>
                  <div className="user-details-view-buttons" role="group" aria-label={t.searchResults.viewType}>
                    <button
                      className={`user-details-view-button ${viewType === 'list' ? 'active' : ''}`}
                      onClick={() => setViewType('list')}
                      type="button"
                      aria-label={t.searchResults.listView}
                      aria-pressed={viewType === 'list'}
                    >
                      <List size={18} aria-hidden="true" />
                    </button>
                    <button
                      className={`user-details-view-button ${viewType === 'grid' ? 'active' : ''}`}
                      onClick={() => setViewType('grid')}
                      type="button"
                      aria-label={t.searchResults.gridView}
                      aria-pressed={viewType === 'grid'}
                    >
                      <Grid2X2 size={17} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className={`user-details-products-grid ${viewType === 'list' ? 'products-list' : ''}`}>
              {productsLoading ? (
                <LoadingIndicator message={t.userDetails.loadingAnnouncements} />
              ) : products.length === 0 ? (
                <EmptyState
                  className="user-details-no-products"
                  icon={<Sprout size={32} />}
                  title={activeTab === 'SELL' ? t.userDetails.emptySaleTitle : t.userDetails.emptyWantedTitle}
                  description={activeTab === 'SELL' ? t.userDetails.emptySaleDescription : t.userDetails.emptyWantedDescription}
                  headingLevel={2}
                  announce
                />
              ) : (
                products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    viewType={viewType}
                    onNavigateToProductDetail={onNavigateToProductDetail}
                    isLoggedIn={isLoggedIn && !isAdmin}
                    onNavigateToLogin={onNavigateToLogin}
                    showFavorite={!isAdmin}
                  />
                ))
              )}
            </div>

            {renderPagination()}
          </div>
        </div>
      </main>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />

      {showReportUserModal && (
        <ReportUserModal
          onClose={handleReportUserClose}
          onSubmit={handleReportUserSubmit}
        />
      )}

      {showImageViewer && user?.imageUrl && (
        <ImageViewer
          images={[user.imageUrl]}
          initialIndex={0}
          onClose={() => setShowImageViewer(false)}
        />
      )}

    </div>
  );
};

export default UserDetails;
